/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { LoadingOutlined } from '@ant-design/icons';
import { InputNumber, Modal, Slider } from 'antd';
import { useEffect, useState } from 'react';

import { Node } from '@/client';
import { GuardedButton } from '@/components/GuardedButton';
import { useMaintenanceMode, UseMaintenanceModeReturn } from '@/hooks/useMaintenanceMode';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Flex } from '@/primitives/Flex';
import { Space } from '@/primitives/Space';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { Tooltip } from '@/primitives/Tooltip';

export type MaintenanceModeProps = {
  nodeId: string;
  node?: Node;
  children: (maintenance: UseMaintenanceModeReturn) => React.ReactNode;
};

function formatElapsedTime(startTime: string): string {
  const start = new Date(startTime).getTime();
  const now = Date.now();
  const elapsedMs = now - start;

  if (elapsedMs < 0) return '0s';

  const seconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

function formatRemainingTime(endTime: string): string {
  const end = new Date(endTime).getTime();
  const now = Date.now();
  const remainingMs = end - now;

  if (remainingMs <= 0) return '0s';

  const seconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

function BannerContent({
  maintenance,
  isAuditMode,
  textColor,
  elapsedTime,
  remainingTime,
  t,
}: {
  maintenance: UseMaintenanceModeReturn;
  isAuditMode: boolean;
  textColor: string;
  elapsedTime: string;
  remainingTime: string;
  t: MimicTranslationFunction<'node'>;
}) {
  if (maintenance.isTransitioning) {
    return (
      <>
        <LoadingOutlined style={{ color: textColor }} />
        <Text style={{ color: textColor, fontWeight: 500 }}>{t('maintenanceModeChanging')}</Text>
      </>
    );
  }

  if (isAuditMode) {
    // Show countdown to scheduled exit if available, otherwise show elapsed time
    const timeDisplay = maintenance.scheduledExitTime && remainingTime ? remainingTime : elapsedTime;
    const timeLabel = maintenance.scheduledExitTime ? t('auditModeScheduledExit') : '';
    return (
      <>
        <Text style={{ color: textColor, fontWeight: 500 }}>{t('nodeInAuditMode')}</Text>
        {timeDisplay && (
          <Text style={{ color: textColor }}>{timeLabel ? `${timeLabel} ${timeDisplay}` : `(${timeDisplay})`}</Text>
        )}
      </>
    );
  }

  return (
    <>
      <Text style={{ color: textColor, fontWeight: 500 }}>{t('nodeInMaintenanceMode')}</Text>
      {maintenance.startTime && elapsedTime && <Text style={{ color: textColor }}>({elapsedTime})</Text>}
    </>
  );
}

export function MaintenanceModeBanner({ maintenance }: { maintenance: UseMaintenanceModeReturn }) {
  const { t } = useMimicTranslation('node');
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [remainingTime, setRemainingTime] = useState<string>('');

  // During transitions, use the mode we're transitioning FROM to maintain consistent styling
  // This prevents jarring color shifts (e.g., orange -> yellow -> gone when exiting audit mode)
  const effectiveMode =
    maintenance.isTransitioning && maintenance.transitioningFromMode
      ? maintenance.transitioningFromMode
      : maintenance.mode;
  const isAuditMode = effectiveMode === 'audit';

  // Elapsed time for maintenance mode
  useEffect(() => {
    if (!maintenance.startTime) {
      return undefined;
    }

    setElapsedTime(formatElapsedTime(maintenance.startTime));

    const interval = setInterval(() => {
      setElapsedTime(formatElapsedTime(maintenance.startTime!));
    }, 1000);

    return () => clearInterval(interval);
  }, [maintenance.startTime]);

  // Remaining time countdown for audit mode
  useEffect(() => {
    if (!maintenance.scheduledExitTime) {
      setRemainingTime('');
      return undefined;
    }

    setRemainingTime(formatRemainingTime(maintenance.scheduledExitTime));

    const interval = setInterval(() => {
      setRemainingTime(formatRemainingTime(maintenance.scheduledExitTime!));
    }, 1000);

    return () => clearInterval(interval);
  }, [maintenance.scheduledExitTime]);

  if (maintenance.mode === 'off' && !maintenance.isTransitioning) {
    return null;
  }

  // Banner styling based on mode
  const bannerStyles = isAuditMode
    ? {
        backgroundColor: '#fff7e6',
        border: '1px solid #ffd591',
      }
    : {
        backgroundColor: '#fffbe6',
        border: '1px solid #ffe58f',
      };
  const textColor = isAuditMode ? '#d46b08' : '#ad6800';

  return (
    <div
      style={{
        ...bannerStyles,
        borderRadius: '6px',
        padding: '8px 16px',
        marginTop: '20px',
        marginBottom: '-16px',
      }}
    >
      <Flex justify="space-between" align="center">
        <Flex align="center" gap="xs">
          <BannerContent
            maintenance={maintenance}
            isAuditMode={isAuditMode}
            textColor={textColor}
            elapsedTime={elapsedTime}
            remainingTime={remainingTime}
            t={t}
          />
        </Flex>
        <GuardedButton
          requiredRole="admin"
          type="primary"
          onClick={isAuditMode ? maintenance.endAuditMode : maintenance.endMaintenance}
          disabled={maintenance.isTransitioning || maintenance.isInteractionBlocked}
          loading={maintenance.isTransitioning}
          dataTestId={isAuditMode ? 'end-audit-mode-button' : 'end-maintenance-mode-button'}
        >
          {isAuditMode ? t('endAuditMode') : t('endMaintenance')}
        </GuardedButton>
      </Flex>
    </div>
  );
}

export function MaintenanceModeButton({ maintenance }: { maintenance: UseMaintenanceModeReturn }) {
  const { t } = useMimicTranslation('node');

  // Hide button when in maintenance, audit, or transitioning
  if (maintenance.mode === 'maintenance' || maintenance.mode === 'audit' || maintenance.isTransitioning) {
    return null;
  }

  const isDisabled = maintenance.isInteractionBlocked || maintenance.isVersionMissing;

  const button = (
    <GuardedButton
      requiredRole="admin"
      type="primary"
      dataTestId="enter-maintenance-mode-button"
      disabled={isDisabled}
      onClick={maintenance.openModal}
    >
      {t('enterMaintenance')}
    </GuardedButton>
  );

  if (maintenance.isVersionMissing) {
    return (
      <Tooltip title={t('maintenanceModeUnsupportedTooltip')}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
}

export function MaintenanceModeModal({ maintenance }: { maintenance: UseMaintenanceModeReturn }) {
  const { t } = useMimicTranslation('node');

  return (
    <Modal
      open={maintenance.isModalOpen}
      onCancel={maintenance.closeModal}
      footer={null}
      data-testid="enter-maintenance-modal"
    >
      <Space dir="vertical" size="md" width="100%">
        <Title level={4}>{t('enterMaintenanceModal.title')}</Title>
        <Text>{t('enterMaintenanceModal.description')}</Text>
        <Flex gap="sm" justify="flex-end">
          <Button type="default" onClick={maintenance.closeModal} dataTestId="maintenance-modal-cancel">
            {t('enterMaintenanceModal.cancel')}
          </Button>
          <Button type="primary" onClick={maintenance.startMaintenance} dataTestId="maintenance-modal-start">
            {t('enterMaintenanceModal.start')}
          </Button>
        </Flex>
      </Space>
    </Modal>
  );
}

export function ExitMaintenanceModeModal({ maintenance }: { maintenance: UseMaintenanceModeReturn }) {
  const { t } = useMimicTranslation('node');
  const [durationHours, setDurationHours] = useState<number>(48);

  const handleEnterAuditMode = () => {
    maintenance.enterAuditMode(durationHours);
  };

  return (
    <Modal
      open={maintenance.isExitMaintenanceModalOpen}
      onCancel={maintenance.closeExitMaintenanceModal}
      footer={null}
      data-testid="exit-maintenance-modal"
    >
      <Space dir="vertical" size="md" width="100%">
        <Title level={4}>{t('exitMaintenanceModal.title')}</Title>
        <Text>{t('exitMaintenanceModal.description')}</Text>
        <Flex vertical gap="xs">
          <Text>{t('exitMaintenanceModal.durationLabel')}</Text>
          <Flex align="center" gap="sm" full>
            <div style={{ flex: 1 }}>
              <Slider
                min={1}
                max={72}
                value={durationHours}
                onChange={(value) => setDurationHours(value)}
                data-testid="audit-duration-slider"
              />
            </div>
            <InputNumber
              min={1}
              max={72}
              value={durationHours}
              onChange={(value) => setDurationHours(value ?? 48)}
              style={{ width: 80 }}
              data-testid="audit-duration-input"
            />
          </Flex>
        </Flex>
        <Text type="subtle">{t('exitMaintenanceModal.durationHelp')}</Text>
        <Text type="subtle">{t('exitMaintenanceModal.skipDescription')}</Text>
        <Flex gap="sm" justify="flex-end">
          <Button
            type="default"
            onClick={maintenance.skipAuditAndEndMaintenance}
            dataTestId="exit-maintenance-skip-audit"
          >
            {t('exitMaintenanceModal.skipAudit')}
          </Button>
          <Button type="primary" onClick={handleEnterAuditMode} dataTestId="exit-maintenance-enter-audit">
            {t('exitMaintenanceModal.continue')}
          </Button>
        </Flex>
      </Space>
    </Modal>
  );
}

/**
 * MaintenanceMode provider component that manages maintenance state and provides
 * it to children via render props pattern.
 *
 * Usage:
 * ```tsx
 * <MaintenanceMode nodeId={nodeId} node={node}>
 *   {(maintenance) => (
 *     <>
 *       <Header button={<MaintenanceModeButton maintenance={maintenance} />} />
 *       <MaintenanceModeBanner maintenance={maintenance} />
 *       <MaintenanceModeModal maintenance={maintenance} />
 *       <ExitMaintenanceModeModal maintenance={maintenance} />
 *     </>
 *   )}
 * </MaintenanceMode>
 * ```
 */
export function MaintenanceMode({ nodeId, node, children }: MaintenanceModeProps) {
  const maintenance = useMaintenanceMode({ nodeId, node });
  return <>{children(maintenance)}</>;
}
